import { Borders, Row, Workbook, Worksheet } from "exceljs"
import moment from "moment"
import { CoaEnum } from "pages/Accounts/Report/ProfitAndLoss/enums"
import { BalanceSheetReportType } from "pages/Finance/Report/types"

type HandleExportExcel = {
  comparisonDates: any[]
  data: BalanceSheetReportType[]
  companyName?: string
  date?: string
  endDate?: string
}

export const handleExportExcelProfitLoss = async ({ comparisonDates, data, companyName, date, endDate }: HandleExportExcel) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const parents = data.filter((item) => item.parent_id === -1)
  const columns = [" "].concat(comparisonDates)
  worksheet.views = [{ showGridLines: false }]

  worksheet.mergeCells(2, 1, 1, columns.length)

  makeTitle(worksheet, companyName, date, endDate)

  const headers = worksheet.addRow(columns)
  headers.font = { bold: true }
  // headers.height = 30
  headers.eachCell((cell) => {
    cell.alignment = { horizontal: "right", vertical: "middle" }
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFFFFFF" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      // left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      // right: { style: "thin", color: { argb: "000000" } }
    }
  })

  worksheet.getColumn(1).width = 50
  worksheet.getColumn(2).width = 60
  worksheet.getColumn(3).width = 30

  for (let i = 4; i <= worksheet.columnCount; i++) worksheet.getColumn(i).width = 20

  const mapData = parents?.map((item) => {
    const hierarchy = buildHierarchy(data, item.coa_id)
    return { ...item, children: hierarchy }
  })

  makeRows(mapData, worksheet)

  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `profit_loss.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

// Helpers
export const buildHierarchy = (data: BalanceSheetReportType[], parentId: number | null): BalanceSheetReportType[] => {
  return data
    .filter((item) => item.parent_id === parentId)
    .map((item) => ({ ...item, children: buildHierarchy(data, item.coa_id) }))
}

export const makeTitle = (worksheet: Worksheet, companyName?: string, date?: string, endDate?: string) => {
  const titleCell = worksheet.getCell(1, 2)

  titleCell.value = "PROFIT AND LOSS"
  titleCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true
  }
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "000000" }
  };
  titleCell.font = { bold: true, size: 15, color: { argb: "FFFFFFFF" } }

  worksheet.getColumn(2).width = 20
  worksheet.addRow([])

  const companyNameCell = worksheet.addRow([companyName?.toUpperCase()])
  const dateCell = worksheet.addRow([`From ${moment(date).format("DD MMM YYYY")} to ${moment(endDate).format("DD MMM YYYY")}`])

  const endColumnIdx = worksheet.columnCount

  worksheet.mergeCells(companyNameCell.number, 1, companyNameCell.number, endColumnIdx)
  worksheet.mergeCells(dateCell.number, 1, dateCell.number, endColumnIdx)

  worksheet.addRow([])

  companyNameCell.eachCell((cell) => {
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true
    }
    cell.font = { bold: true, size: 15 }
  })

  dateCell.eachCell((cell) => {
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true
    }
    cell.font = { size: 12 }
  })
}

export const makeRows = (data: any, worksheet: Worksheet) => {
  let itemRow: Row

  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    // left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    // right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item: any) => {
    const indent = " ".repeat(item.coa_level * 4)

    if (item.header_level === 3) {
      // itemRow = worksheet.addRow([`${indent}${item.coacode}`, `${indent}${item.coa_name}`].concat(item.balance))
      itemRow = worksheet.addRow([`${indent}${item.coa_name}`].concat(item.balance))
    } else if ([CoaEnum.NET_PROFIT, CoaEnum.NET_ASSETS].includes(item.coa_name)) {
      // itemRow = worksheet.addRow([`${indent}${item.coacode}`, `${indent}${item.coa_name}`].concat(item.balance))
      itemRow = worksheet.addRow([`${indent}${item.coa_name}`].concat(item.balance))
      itemRow.font = { bold: true }
    } else {
      // itemRow = worksheet.addRow([`${indent}${item.coacode}`, `${indent}${item.coa_name}`].concat(item.balance?.map(() => "")))
      itemRow = worksheet.addRow([`${indent}${item.coa_name}`].concat(item.balance?.map(() => "")))
      itemRow.font = { bold: true }
    }

    itemRow.eachCell((cell) => cell.border = cellBorder)

    if (item.children?.length > 0) makeRows(item.children, worksheet)

    if (item?.children && item?.children?.length > 0 && item.coa_level !== 3 && ![CoaEnum.NET_PROFIT, CoaEnum.NET_ASSETS].includes(item.coa_name)) {
      // const totalRow = worksheet.addRow([` TOTAL ${item.coa_name}`, "", ...item.balance])
      const totalRow = worksheet.addRow([` TOTAL ${item.coa_name}`, ...item.balance])
      totalRow.font = { bold: true }
      totalRow.height = 20
      totalRow.alignment = { vertical: "middle" }
      // worksheet.mergeCells(`A${totalRow.number}:B${totalRow.number}`)
      totalRow.eachCell((cell) => cell.border = cellBorder)

      if (item.coa_name?.toUpperCase() === CoaEnum.COST_OF_SALES) {
        const income = data.find((d: any) => d.coa_name?.toUpperCase() === CoaEnum.INCOME)?.balance ?? []
        const cogs = item.balance ?? []
        const grossProfit = income.map((val: number, idx: number) => (val || 0) - (cogs[idx] || 0))

        // const grossRow = worksheet.addRow(["GROSS PROFIT", "", ...grossProfit])
        const grossRow = worksheet.addRow(["GROSS PROFIT", ...grossProfit])
        
        // worksheet.mergeCells(grossRow.number, 1, grossRow.number, 2);

        grossRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
        grossRow.eachCell((cell, colNumber) => {
          cell.border = cellBorder;
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "000000" }
          };
          cell.alignment = {
            vertical: "middle",
            // horizontal: colNumber <= 2 ? "left" : "right",
            horizontal: colNumber === 1 ? "left" : "right",
          };
        });
      }

      if (item.coa_name?.toUpperCase() === CoaEnum.EXPENSES) {
        const totalOperatingExpenses: number[] = data.find((d: any) => d.coa_name?.toUpperCase() === CoaEnum.OPERATING_EXPENSES)?.balance ?? [];

        const income = data.find((d: any) => d.coa_name?.toUpperCase() === CoaEnum.INCOME)?.balance ?? [];
        const cogs = data.find((d: any) => d.coa_name?.toUpperCase() === CoaEnum.COST_OF_SALES)?.balance ?? [];
        const grossProfit = income.map((val: number, idx: number) => (val || 0) - (cogs[idx] || 0));

        const ebitda = grossProfit.map((gp: number, idx: number) => gp - (totalOperatingExpenses[idx] ?? 0));

        // const ebitdaRow = worksheet.addRow(["EBITDA", "", ...ebitda]);
        const ebitdaRow = worksheet.addRow(["EBITDA", ...ebitda]);

        // worksheet.mergeCells(ebitdaRow.number, 1, ebitdaRow.number, 2);

        ebitdaRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
        ebitdaRow.eachCell((cell, colNumber) => {
          cell.border = cellBorder;
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "000000" }
          };
          cell.alignment = {
            vertical: "middle",
            // horizontal: colNumber <= 2 ? "left" : "right",
            horizontal: colNumber === 1 ? "left" : "right",
          };
        });
      }
    }
  })
}
